import * as formik from 'formik';
import * as yup from 'yup';
import { useState, useEffect } from 'react';
import { Modal, Button, InputGroup, Form, Row, Col } from 'react-bootstrap';
import { createEntity, editEntity, getEntityForm } from '../../utils/api';
import Loader from '../Loader';
import Success from '../Success'
import Error from '../Error'
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EditEntityDocumentSettingsModal from './EditEntityDocumentSettingsModal';

const CreateEditEntityModal = ({ show, onHide, entity }) => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [showEditEntityDocumentSettingsModal, setShowEditEntityDocumentSettingsModal] = useState(false);

    const isCreate = !entity;
    entity = entity || {};

    const fetchData = async () => {
        setLoading(true);
        const data = await getEntityForm();
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    useEffect(() => {
        const onLoad = () => {
            if (show)
                fetchData();
            else
                resetState();
        }
        onLoad();
    }, [show]);

    const resetState = () => {
        if (!show) {
            setError(null);
            setLoading(false);
        }
    };
    const closeModal = (shouldRefresh) => {
        setLoading(true);
        setIsSaving(false);
        setError(false);
        setIsSaved(false);
        onHide(shouldRefresh);
    }
    const showDocumentSettings = () => {
        setShowEditEntityDocumentSettingsModal(true);
    }
    const { Formik } = formik;

    const schema = yup.object().shape({
        name: yup.string().required('This field is required'),
        legalName: yup.string().required('This field is required'),
        taxId: yup.string().required('This field is required'),
        addressLine1: yup.string().required('This field is required'),
        city: yup.string().required('This field is required'),
        state: yup.string().required('This field is required'),
        postalCode: yup.string().required('This field is required'),
    });



    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop={true} style={{ zIndex: 1051 }} centered>
            <Modal.Header closeButton>
                <Modal.Title>{isCreate ? 'Create Entity' : 'Edit Entity (Id = ' + entity?.Id + ")"}</Modal.Title>
            </Modal.Header>

            {loading || isSaving ? (
                <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader showText={true} text={isSaving && 'Saving...'} />
                </div>
            ) : (
                error ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Error />
                    </div>
                ) : isSaved ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Success />
                    </div>
                ) : (
                    <Modal.Body>
                        <Formik
                            validationSchema={schema}
                            onSubmit={async (form) => {
                                form.id = entity?.Id; 
                                
                                setLoading(true);
                                const res = isCreate ? await createEntity(form) : await editEntity(form);
                                if (res) {
                                    setIsSaved(true);
                                }
                                else {
                                    setError(true);
                                }
                                setIsSaving(false);
                                setLoading(false);

                                if (res)
                                    setTimeout(() => closeModal(true), 2000);
                            }}
                            initialValues={{
                                name: entity?.Name || '',
                                legalName: entity?.LegalName || '',
                                taxId: entity?.TaxId || '',
                                addressLine1: entity?.Address?.AddressLine1 || '',
                                addressLine2: entity?.Address?.AddressLine2 || '',
                                city: entity?.Address?.City || '',
                                state: entity?.Address?.State || '',
                                postalCode: entity?.Address?.PostalCode || '',
                                documentFooterText: entity?.FooterText || ''
                            }}
                        >
                            {({ handleSubmit, handleChange, values, touched, errors }) => (

                                <Form noValidate onSubmit={handleSubmit}>
                                    <Row>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="name" value={values.name} onChange={handleChange} isInvalid={errors.name} isValid={values.name && !errors.name} aria-label="Name" />
                                                <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="name"><span style={{ color: 'red' }}>*</span>Name</Form.Label>
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="legalName" value={values.legalName} onChange={handleChange} isInvalid={errors.legalName} isValid={values.legalName && !errors.legalName} aria-label="Legal Name" />
                                                <Form.Control.Feedback type='invalid'>{errors.legalName}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="legalName"><span style={{ color: 'red' }}>*</span>Legal Name</Form.Label>
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="taxId" value={values.taxId} onChange={handleChange} isInvalid={errors.taxId} isValid={values.taxId && !errors.taxId} aria-label="Tax Id Number (EIN, SSN, etc.)" />
                                                <Form.Control.Feedback type='invalid'>{errors.taxId}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="taxId">Tax Id (EIN, SSN, etc.)</Form.Label>
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <Form.Group>
                                                <Form.Control id="addressLine1" value={values.addressLine1} onChange={handleChange} isInvalid={errors.addressLine1} isValid={values.addressLine1 && !errors.addressLine1} aria-label="Address" />
                                                <Form.Control.Feedback type='invalid'>{errors.addressLine1}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="addressLine1"><span style={{ color: 'red' }}>*</span>Address</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control id="addressLine2" value={values.addressLine2} onChange={handleChange} isInvalid={errors.addressLine2} isValid={values.addressLine2 && !errors.addressLine2} aria-label="Address 2" />
                                                <Form.Control.Feedback type='invalid'>{errors.addressLine2}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="addressLine2">Address 2</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control id="city" value={values.city} onChange={handleChange} isInvalid={errors.city} isValid={values.city && !errors.city} aria-label="City" />
                                                <Form.Control.Feedback type='invalid'>{errors.city}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="city"><span style={{ color: 'red' }}>*</span>City</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control
                                                    id='state'
                                                    as="select"
                                                    value={values.state}
                                                    isInvalid={errors.state} isValid={values.state && !errors.state} aria-label="State"
                                                    onChange={handleChange}
                                                >
                                                    <option value="">Select a state</option>
                                                    {data?.States?.map((o, index) => (
                                                        <option key={o.Code} value={o.Code}>{o.Name}</option>
                                                    ))}
                                                </Form.Control>
                                                <Form.Control.Feedback type='invalid'>{errors.state}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="state"><span style={{ color: 'red' }}>*</span>State</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={6} md={4} lg={3}>
                                            <Form.Group>
                                                <Form.Control id="postalCode" value={values.postalCode} onChange={handleChange} isInvalid={errors.postalCode} isValid={values.postalCode && !errors.postalCode} aria-label="Zip" />
                                                <Form.Control.Feedback type='invalid'>{errors.postalCode}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="postalCode"><span style={{ color: 'red' }}>*</span>Zip</Form.Label>
                                        </Col>
                                        <Col xs={12} sm={12} md={12} lg={12}>
                                            <Form.Group>
                                                <Form.Control id="documentFooterText" value={values.documentFooterText} onChange={handleChange} isInvalid={errors.documentFooterText} isValid={values.documentFooterText && !errors.documentFooterText} aria-label="Document Footer Text" />
                                                <Form.Control.Feedback type='invalid'>{errors.documentFooterText}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="documentFooterText">Document Footer Text</Form.Label>
                                        </Col>
                                    </Row>
                                    <br />
                                    <Row>
                                        <Col className="d-flex justify-content-start">
                                            <Button onClick={showDocumentSettings} className="me-2">
                                                <FontAwesomeIcon icon={faGear} className="me-2" />
                                                Document Logo
                                            </Button>
                                        </Col>
                                        <Col className="d-flex justify-content-end">
                                            <Button type="submit">{isCreate ? "Create entity" : "Save"}</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </Modal.Body>
                ))}

                <EditEntityDocumentSettingsModal
                        show={showEditEntityDocumentSettingsModal}
                        onHide={() => {
                            setShowEditEntityDocumentSettingsModal(false);
                        }}
                        entity={entity}
                    />
        </Modal>
    );
};

export default CreateEditEntityModal;